import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

/**
 * The family's custody parents, read from the `custody_parents` setting.
 *
 * The first name is the household parent: the kids listed in a day's
 * `kids_present` are with them. The backend labels custody days
 * "With <name>" and, when the family never configured parents, uses the
 * neutral "With Parent A" / "With Parent B" (codes `parent_a` / `parent_b`).
 * Components compare against these instead of hardcoded names so every
 * family gets its own labels.
 */
export type CustodySide = 'primary' | 'other'

export interface CustodyParentsConfig {
  configured: boolean
  primary: string
  other: string
  parents: string[]
}

export const NEUTRAL_CUSTODY_PARENTS: CustodyParentsConfig = {
  configured: false,
  primary: 'Parent A',
  other: 'Parent B',
  parents: ['Parent A', 'Parent B'],
}

/**
 * Normalizes a list setting. Accepts a decoded array or a JSON-encoded
 * string; anything else (including the old run-together "TomJulie" form)
 * is treated as unset.
 */
export function parseSettingList(value: unknown): string[] {
  let list: unknown = value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed.startsWith('[')) return []
    try {
      list = JSON.parse(trimmed)
    } catch {
      return []
    }
  }
  if (!Array.isArray(list)) return []
  return list
    .filter((v): v is string => typeof v === 'string')
    .map(v => v.trim())
    .filter(v => v !== '')
}

export function buildCustodyParents(value: unknown): CustodyParentsConfig {
  const names = parseSettingList(value)
  if (names.length < 2) return NEUTRAL_CUSTODY_PARENTS
  return { configured: true, primary: names[0], other: names[1], parents: names }
}

/** Same rule as the backend's `CustodyParents.location_code/2`. */
export function custodyLocationCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Which side of the schedule a backend label ("With Tom", "With Parent B")
 * or location code ("tom", "parent_b") refers to. Returns null for anything
 * else, such as "With Friend".
 */
export function custodySideOf(
  value: string | null | undefined,
  cfg: CustodyParentsConfig
): CustodySide | null {
  if (!value) return null
  const text = value.trim()
  const name = text.replace(/^with\s+/i, '').toLowerCase()
  const code = custodyLocationCode(text)

  const primaryNames = [cfg.primary.toLowerCase(), NEUTRAL_CUSTODY_PARENTS.primary.toLowerCase()]
  const otherNames = [cfg.other.toLowerCase(), NEUTRAL_CUSTODY_PARENTS.other.toLowerCase()]
  const primaryCodes = [custodyLocationCode(cfg.primary), 'parent_a']
  const otherCodes = [custodyLocationCode(cfg.other), 'parent_b']

  if (primaryNames.includes(name) || primaryCodes.includes(code)) return 'primary'
  if (otherNames.includes(name) || otherCodes.includes(code)) return 'other'
  return null
}

export function custodyLabel(cfg: CustodyParentsConfig, side: CustodySide): string {
  return `With ${side === 'primary' ? cfg.primary : cfg.other}`
}

export const custodyParentsKey = ['settings', 'custody_parents'] as const

async function fetchCustodyParents(): Promise<CustodyParentsConfig> {
  try {
    const response = await apiClient.get('/api/settings/custody_parents')
    return buildCustodyParents(response?.value)
  } catch {
    // 404 when the family never set it; neutral labels either way.
    return NEUTRAL_CUSTODY_PARENTS
  }
}

export function useCustodyParents(): CustodyParentsConfig & { loading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: custodyParentsKey,
    queryFn: fetchCustodyParents,
    staleTime: 5 * 60 * 1000,
  })
  return { ...(data ?? NEUTRAL_CUSTODY_PARENTS), loading: isLoading }
}
